import { useMemo, useState } from 'react';

type FinanceStats = {
  revenue: number;
  commission: number;
  pending: number;
  tickets: number;
};

type FinanceTransaction = {
  id: string;
  reference: string;
  event: {
    id: number;
    name: string;
    category: string;
    image?: string | null;
  };
  organizer: string;
  buyer: string;
  buyer_email?: string | null;
  buyer_phone?: string | null;
  buyer_address?: string | null;
  tickets: number;
  total: number;
  commission: number;
  payment_method: string;
  status: 'completed' | 'pending' | 'cancelled';
  payment_status: string;
  date: string;
};

const EVENT_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=event%20stage%20crowd%20conference%20clean&width=80&height=80&seq=admin-event-finance-fallback&orientation=squarish';
const DEXPAY_RATE = 0.02;

const formatMoney = (value: number) => `${value.toLocaleString('fr-FR')} FCFA`;
const formatDate = (value: string) =>
  new Date(value).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const estimateDexpayFee = (total: number) => Math.ceil(Math.max(0, Number(total) || 0) * DEXPAY_RATE);

const paymentMethodLabel = (value: string) => {
  if (value === 'orange_money') return 'Orange Money';
  if (value === 'wave') return 'Wave';
  if (value === 'mixx_by_yas') return 'Mixx by Yas';
  if (value === 'dexpay') return 'DexPay';
  if (value === 'mobile_money') return 'Mobile Money';
  if (value === 'mtn_momo') return 'MTN MoMo';
  if (value === 'bank_transfer') return 'Virement';
  return value.replace(/_/g, ' ');
};
const transactionPaymentMethodLabel = (transaction: Pick<FinanceTransaction, 'payment_method' | 'payment_status'>) => (
  transaction.payment_status === 'completed'
    ? paymentMethodLabel(transaction.payment_method)
    : 'DexPay (choix non confirmé)'
);

const statusBadgeClass: Record<FinanceTransaction['status'], string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-orange-100 text-orange-700',
};

const statusLabel: Record<FinanceTransaction['status'], string> = {
  completed: 'Paiement réussi',
  pending: 'Paiement non validé',
  cancelled: 'Annulé',
};

const statusDotClass: Record<FinanceTransaction['status'], string> = {
  completed: 'bg-emerald-500',
  pending: 'bg-amber-400',
  cancelled: 'bg-orange-400',
};

const paymentMethodClass = (value: string) => {
  if (value === 'orange_money') return 'bg-orange-100 text-orange-700';
  if (value === 'wave') return 'bg-indigo-100 text-indigo-700';
  if (value === 'mixx_by_yas') return 'bg-yellow-100 text-yellow-800';
  if (value === 'dexpay') return 'bg-blue-100 text-blue-700';
  if (value === 'mtn_momo') return 'bg-yellow-100 text-yellow-700';
  if (value === 'mobile_money') return 'bg-sky-100 text-sky-700';
  if (value === 'bank_transfer') return 'bg-slate-100 text-slate-700';
  return 'bg-gray-100 text-gray-600';
};

export default function AdminEventFinance({
  stats,
  transactions,
  onDelete,
}: {
  stats: FinanceStats;
  transactions: FinanceTransaction[];
  onDelete: (transaction: FinanceTransaction) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | FinanceTransaction['status']>('all');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<FinanceTransaction | null>(null);
  const paidTransactions = useMemo(
    () => transactions.filter((transaction) => transaction.status === 'completed' && transaction.payment_status === 'completed'),
    [transactions]
  );

  const filteredTransactions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return transactions.filter((transaction) => {
      const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
      const matchesSearch =
        normalizedSearch.length === 0 ||
        transaction.reference.toLowerCase().includes(normalizedSearch) ||
        transaction.event.name.toLowerCase().includes(normalizedSearch) ||
        transaction.organizer.toLowerCase().includes(normalizedSearch) ||
        transaction.buyer.toLowerCase().includes(normalizedSearch);

      return matchesStatus && matchesSearch;
    });
  }, [search, statusFilter, transactions]);

  const visibleCountLabel = `${filteredTransactions.length} transaction(s)`;
  const completedDexpayFees = paidTransactions
    .reduce((sum, transaction) => sum + estimateDexpayFee(transaction.total), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {[
          { label: 'Revenus événements', value: formatMoney(stats.revenue), icon: 'ri-calendar-check-line', color: 'bg-gold-100 text-gold-dark' },
          { label: 'Commissions FSN', value: formatMoney(stats.commission), icon: 'ri-percent-line', color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Frais DexPay (2%)', value: formatMoney(completedDexpayFees), icon: 'ri-bank-card-line', color: 'bg-blue-100 text-blue-700' },
          { label: 'Tickets payés', value: String(paidTransactions.reduce((sum, transaction) => sum + transaction.tickets, 0)), icon: 'ri-ticket-2-line', color: 'bg-gold-100 text-gold-dark' },
          { label: 'Paiements non validés', value: String(stats.pending), icon: 'ri-error-warning-line', color: 'bg-amber-100 text-amber-700' },
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-[#eef1f5] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${card.color}`}>
              <i className={`${card.icon} text-lg`}></i>
            </div>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="mt-1 text-2xl font-bold text-navy">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#eef1f5] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 border-b border-[#eef1f5] px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-100">
              <i className="ri-ticket-line text-gold-dark text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Transactions Tickets / Événements</h3>
              <p className="text-xs text-gray-400">{visibleCountLabel}</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 xl:items-end">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Chercher..."
                  className="w-full rounded-xl border border-[#e5e7eb] bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 xl:w-64"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters((current) => !current)}
                className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${showFilters || statusFilter !== 'all' ? 'border-navy bg-navy text-white' : 'border-[#e5e7eb] bg-white text-navy hover:bg-gray-50'}`}
              >
                <i className="ri-filter-3-line"></i>
                Filtre
              </button>
            </div>
            {showFilters ? (
              <div className="flex flex-wrap items-center gap-1 rounded-xl bg-[#f5f7fa] p-1">
                {([
                  { key: 'all', label: 'Tous' },
                  { key: 'completed', label: 'Paiement réussi' },
                  { key: 'pending', label: 'Paiement non validé' },
                  { key: 'cancelled', label: 'Annulé' },
                ] as const).map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setStatusFilter(item.key)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${statusFilter === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="divide-y divide-[#f2f4f7] md:hidden">
          {filteredTransactions.map((transaction) => (
            <button
              key={transaction.id}
              type="button"
              onClick={() => setSelectedTransaction(transaction)}
              className="w-full px-5 py-4 text-left transition-colors hover:bg-[#fafbfd]"
            >
              <div className="flex items-start gap-3">
                <img
                  src={transaction.event.image || EVENT_FALLBACK_IMAGE}
                  alt={transaction.event.name}
                  className="h-12 w-12 flex-shrink-0 rounded-xl bg-gray-100 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-gray-900">{transaction.event.name}</p>
                      <p className="mt-0.5 text-xs font-semibold text-gold-dark">{transaction.reference}</p>
                    </div>
                    <span className="whitespace-nowrap text-sm font-bold text-navy">{formatMoney(transaction.total)}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${paymentMethodClass(transaction.payment_method)}`}>
                      {transactionPaymentMethodLabel(transaction)}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass[transaction.status]}`}>
                      <span className={`h-2 w-2 rounded-full ${statusDotClass[transaction.status]}`}></span>
                      {statusLabel[transaction.status]}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-400">
                    <span>{formatDate(transaction.date)}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-navy">
                      Détails
                      <i className="ri-arrow-right-s-line text-base"></i>
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}

          {filteredTransactions.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              Aucune transaction événement trouvée.
            </div>
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1280px]">
            <thead>
              <tr className="border-b border-[#f2f4f7] bg-[#fcfcfd]">
                {['Code ticket', 'Événement', 'Organisateur', 'Acheteur', 'Tickets', 'Total', 'DexPay (2%)', 'Commission', 'Paiement', 'Statut', 'Date', 'Actions'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction) => (
                <tr key={transaction.id} className="border-b border-[#f4f6f8] transition-colors hover:bg-[#fafbfd]">
                  <td className="px-4 py-4 text-xs font-semibold text-gold-dark">{transaction.reference}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={transaction.event.image || EVENT_FALLBACK_IMAGE}
                        alt={transaction.event.name}
                        className="h-11 w-11 rounded-xl object-cover bg-gray-100 flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{transaction.event.name}</p>
                        <p className="text-xs text-gray-400">{transaction.event.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{transaction.organizer}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">{transaction.buyer}</p>
                    {transaction.buyer_email ? <p className="text-xs text-gray-400">{transaction.buyer_email}</p> : null}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{transaction.tickets}</td>
                  <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(transaction.total)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-700">{formatMoney(estimateDexpayFee(transaction.total))}</td>
                  <td className="px-4 py-3 text-sm font-bold text-emerald-600">{formatMoney(transaction.commission)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${paymentMethodClass(transaction.payment_method)}`}>
                      {transactionPaymentMethodLabel(transaction)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass[transaction.status]}`}>
                      <span className={`h-2 w-2 rounded-full ${statusDotClass[transaction.status]}`}></span>
                      {statusLabel[transaction.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{formatDate(transaction.date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setSelectedTransaction(transaction)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-navy/10 hover:text-navy"
                        title="Voir le détail"
                      >
                        <i className="ri-eye-line text-base"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(transaction)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        title="Supprimer la transaction"
                        aria-label={`Supprimer ${transaction.reference}`}
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredTransactions.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              Aucune transaction événement trouvée.
            </div>
          )}
        </div>
      </div>

      {selectedTransaction ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gold-dark">Détail transaction événement</p>
                <h3 className="mt-1 text-xl font-bold text-navy">{selectedTransaction.reference}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTransaction(null)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              <div className="mb-5 flex items-center gap-4 rounded-2xl bg-[#f8fafc] p-4">
                <img
                  src={selectedTransaction.event.image || EVENT_FALLBACK_IMAGE}
                  alt={selectedTransaction.event.name}
                  className="h-16 w-16 rounded-xl bg-gray-100 object-cover"
                />
                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-gray-900">{selectedTransaction.event.name}</p>
                  <p className="text-sm text-gray-500">{selectedTransaction.event.category}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[
                  ['Organisateur', selectedTransaction.organizer],
                  ['Acheteur', selectedTransaction.buyer],
                  ['Email acheteur', selectedTransaction.buyer_email || 'Non renseigné'],
                  ['Téléphone acheteur', selectedTransaction.buyer_phone || 'Non renseigné'],
                  ['Adresse acheteur', selectedTransaction.buyer_address || 'Non renseigné'],
                  ['Tickets', String(selectedTransaction.tickets)],
                  ['Total payé', formatMoney(selectedTransaction.total)],
                  ['Frais DexPay (2%)', formatMoney(estimateDexpayFee(selectedTransaction.total))],
                  ['Commission FSN', formatMoney(selectedTransaction.commission)],
                  ['Moyen de paiement', transactionPaymentMethodLabel(selectedTransaction)],
                  ['Statut', statusLabel[selectedTransaction.status]],
                  ['Statut paiement brut', selectedTransaction.payment_status],
                  ['Date', formatDate(selectedTransaction.date)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-gray-100 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
